import { arrayMove } from '@dnd-kit/sortable';
import type {
  EFormReportCatalog,
  EFormReportChartType,
  EFormReportDimension,
  EFormReportFilter,
  EFormReportMeasure,
  EFormReportQuery,
  ReportableField,
} from './useData';

export type Zone = 'rows' | 'columns' | 'values' | 'filters';
export type MeasureFn = EFormReportMeasure['fn'];

export type PlacedDim = EFormReportDimension & { uid: string };
export type PlacedMeasure = EFormReportMeasure & { uid: string };
export type PlacedFilter = EFormReportFilter & { uid: string };

export type ReportPivotDraft = {
  rows: PlacedDim[];
  columns: PlacedDim[];
  values: PlacedMeasure[];
  filters: PlacedFilter[];
  showTotals: boolean;
  chartType: EFormReportChartType;
};

export const MEASURE_FN_LABELS: Record<MeasureFn, string> = {
  count: 'Count',
  count_distinct: 'Distinct',
  sum: 'Sum',
  avg: 'Average',
  min: 'Min',
  max: 'Max',
  pct_true: '% True',
};

export const BUCKET_OPTIONS = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'year', label: 'Year' },
] as const;

export const FILTER_OP_LABELS: Record<string, string> = {
  eq: '=',
  neq: '≠',
  in: 'in',
  contains: 'contains',
  gte: '≥',
  lte: '≤',
  between: 'between',
  empty: 'empty',
  notEmpty: 'not empty',
};

export function uid(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function flattenFields(fields: ReportableField[]): ReportableField[] {
  const seen = new Set<string>();
  const out: ReportableField[] = [];
  const push = (field: ReportableField) => {
    const identity = fieldToken(field);
    if (field.role === 'none' || seen.has(identity)) return;
    seen.add(identity);
    out.push(field);
  };
  for (const field of fields) {
    push(field);
    for (const nested of field.nestedFields || []) {
      push({ ...nested, label: `${field.label} · ${nested.label}` });
    }
  }
  return out;
}

export function measureFnsFor(dataType?: string, metaKey?: string): MeasureFn[] {
  if (metaKey === 'submissionCount') return ['count'];
  if (metaKey === 'submittedAt' || dataType === 'date' || dataType === 'time') {
    return ['min', 'max', 'count', 'count_distinct'];
  }
  if (dataType === 'number') return ['sum', 'avg', 'min', 'max', 'count', 'count_distinct'];
  if (dataType === 'bool') return ['pct_true', 'count'];
  if (dataType === 'subform') return ['sum', 'avg', 'count'];
  return ['count', 'count_distinct'];
}

export function measureFnLabel(fn: MeasureFn, dataType?: string, metaKey?: string): string {
  if ((metaKey === 'submittedAt' || dataType === 'date' || dataType === 'time') && fn === 'min') {
    return 'Earliest';
  }
  if ((metaKey === 'submittedAt' || dataType === 'date' || dataType === 'time') && fn === 'max') {
    return 'Latest';
  }
  return MEASURE_FN_LABELS[fn] || fn;
}

export function sameDim(a: EFormReportDimension, b: EFormReportDimension) {
  if (a.metaKey && b.metaKey) return a.metaKey === b.metaKey;
  if (a.fieldId && b.fieldId) {
    return a.fieldId === b.fieldId && a.relationId === b.relationId;
  }
  return false;
}

export function fieldToken(field: ReportableField): string {
  return `${field.relation?.id ?? 'base'}:${field.fieldId}`;
}

export function parseToken(id: string):
  | { type: 'source'; kind: 'field' | 'meta'; key: string }
  | { type: 'placed'; zone: Zone; uid: string }
  | { type: 'zone'; zone: Zone | 'fields' }
  | null {
  if (id.startsWith('src:meta:')) return { type: 'source', kind: 'meta', key: id.slice(9) };
  if (id.startsWith('src:field:')) return { type: 'source', kind: 'field', key: id.slice(10) };
  if (id.startsWith('row:')) return { type: 'placed', zone: 'rows', uid: id.slice(4) };
  if (id.startsWith('col:')) return { type: 'placed', zone: 'columns', uid: id.slice(4) };
  if (id.startsWith('val:')) return { type: 'placed', zone: 'values', uid: id.slice(4) };
  if (id.startsWith('flt:')) return { type: 'placed', zone: 'filters', uid: id.slice(4) };
  if (id === 'zone:rows') return { type: 'zone', zone: 'rows' };
  if (id === 'zone:columns') return { type: 'zone', zone: 'columns' };
  if (id === 'zone:values') return { type: 'zone', zone: 'values' };
  if (id === 'zone:filters') return { type: 'zone', zone: 'filters' };
  if (id === 'zone:fields') return { type: 'zone', zone: 'fields' };
  return null;
}

export function defaultDraft(): ReportPivotDraft {
  return {
    rows: [{ uid: uid(), metaKey: 'submittedAt', bucket: 'month' }],
    columns: [],
    values: [{ uid: uid(), fn: 'count', metaKey: 'submissionCount', alias: 'count' }],
    filters: [],
    showTotals: true,
    chartType: 'table',
  };
}

function stripUid<T extends { uid: string }>(items: T[]): Array<Omit<T, 'uid'>> {
  return items.map(({ uid: _uid, ...rest }) => rest);
}

export function toQuery(
  draft: ReportPivotDraft,
  opts?: { approvedOnly?: boolean },
): EFormReportQuery {
  return {
    scope: 'current_schema',
    statuses: ['SUBMITTED'],
    approvedOnly: Boolean(opts?.approvedOnly),
    filters: stripUid(draft.filters),
    dimensions: stripUid(draft.rows),
    columnDimensions: stripUid(draft.columns),
    measures: stripUid(draft.values),
    chartType: draft.chartType,
    showTotals: draft.showTotals,
    limit: draft.columns.length ? 2000 : 200,
  };
}

export function fromQuery(query: EFormReportQuery, chartType?: string): ReportPivotDraft {
  const ct = String(chartType || query.chartType || 'table').toLowerCase();
  return {
    rows: (query.dimensions || []).map((d) => ({ ...d, uid: uid() })),
    columns: (query.columnDimensions || []).map((d) => ({ ...d, uid: uid() })),
    values: (
      query.measures?.length
        ? query.measures
        : [{ fn: 'count' as const, metaKey: 'submissionCount', alias: 'count' }]
    ).map((m) => ({ ...m, uid: uid(), fn: m.fn })),
    filters: (query.filters || []).map((f) => ({ ...f, uid: uid() })),
    showTotals: query.showTotals !== false,
    chartType: ct === 'bar' || ct === 'line' || ct === 'pie' ? ct : 'table',
  };
}

export type CatalogLookups = {
  findField: (fieldId?: string, relationId?: number) => ReportableField | undefined;
  findFieldToken: (token: string) => ReportableField | undefined;
  findMeta: (
    metaKey?: string,
  ) => EFormReportCatalog['meta'][number] | undefined;
};

export function createCatalogLookups(catalog: EFormReportCatalog): CatalogLookups {
  const allFields = flattenFields(catalog.fields ?? []);
  return {
    findField: (fieldId, relationId) =>
      allFields.find(
        (f) =>
          f.fieldId === fieldId &&
          (relationId != null ? f.relation?.id === relationId : !f.relation),
      ),
    findFieldToken: (token) => allFields.find((field) => fieldToken(field) === token),
    findMeta: (metaKey) => catalog.meta.find((m) => m.metaKey === metaKey),
  };
}

export function dimLabel(d: EFormReportDimension, lookups: CatalogLookups) {
  if (d.metaKey) return lookups.findMeta(d.metaKey)?.label || d.metaKey;
  return lookups.findField(d.fieldId, d.relationId)?.label || d.fieldKey || 'Field';
}

export function measureLabel(m: EFormReportMeasure, lookups: CatalogLookups) {
  if (m.metaKey === 'submissionCount') return 'Submissions';
  if (m.metaKey === 'submittedAt') return 'Submitted at';
  return lookups.findField(m.fieldId, m.relationId)?.label || m.fieldKey || 'Field';
}

export function filterLabel(f: EFormReportFilter, lookups: CatalogLookups) {
  if (f.metaKey) return lookups.findMeta(f.metaKey)?.label || f.metaKey;
  return lookups.findField(f.fieldId, f.relationId)?.label || f.fieldKey || 'Field';
}

export function dimDataType(d: EFormReportDimension, lookups: CatalogLookups) {
  return d.metaKey
    ? lookups.findMeta(d.metaKey)?.dataType
    : lookups.findField(d.fieldId, d.relationId)?.dataType;
}

export function isBucketable(d: EFormReportDimension, lookups: CatalogLookups) {
  return Boolean(lookups.findMeta(d.metaKey)?.bucketable) || dimDataType(d, lookups) === 'date';
}

export function buildDim(
  kind: 'field' | 'meta',
  key: string,
  lookups: CatalogLookups,
): PlacedDim | null {
  if (kind === 'meta') {
    const meta = lookups.findMeta(key);
    if (!meta || meta.role === 'measure') return null;
    const next: PlacedDim = { uid: uid(), metaKey: key };
    if (meta.bucketable) next.bucket = 'month';
    return next;
  }
  const field = lookups.findFieldToken(key);
  if (!field || field.role === 'measure' || field.role === 'none') return null;
  return {
    uid: uid(),
    fieldId: field.fieldId,
    relationId: field.relation?.id,
    sourceDefinitionId: field.definitionId,
    expandChecklist: field.dataType === 'multi_enum',
    bucket: field.dataType === 'date' ? 'month' : undefined,
  };
}

export function buildMeasure(
  kind: 'field' | 'meta',
  key: string,
  lookups: CatalogLookups,
  preferredFn?: MeasureFn,
): PlacedMeasure | null {
  if (kind === 'meta') {
    if (key === 'submissionCount') {
      const fn = preferredFn && measureFnsFor(undefined, key).includes(preferredFn) ? preferredFn : 'count';
      return {
        uid: uid(),
        fn,
        metaKey: 'submissionCount',
        alias: fn === 'count' ? 'count' : `${fn}_submissionCount`,
      };
    }
    if (key === 'submittedAt') {
      const allowed = measureFnsFor('date', 'submittedAt');
      const fn = preferredFn && allowed.includes(preferredFn) ? preferredFn : 'max';
      return { uid: uid(), fn, metaKey: 'submittedAt', alias: `${fn}_submittedAt` };
    }
    return null;
  }
  const field = lookups.findFieldToken(key);
  if (!field || field.role === 'none') return null;
  const allowed = measureFnsFor(field.dataType);
  const fn = preferredFn && allowed.includes(preferredFn) ? preferredFn : allowed[0];
  return {
    uid: uid(),
    fn,
    fieldId: field.fieldId,
    relationId: field.relation?.id,
    sourceDefinitionId: field.definitionId,
    alias: `${fn}_${field.fieldKey}`,
  };
}

function sameMeasureSource(a: PlacedMeasure, b: PlacedMeasure) {
  if (a.metaKey && b.metaKey) return a.metaKey === b.metaKey;
  if (a.fieldId && b.fieldId) {
    return a.fieldId === b.fieldId && a.relationId === b.relationId;
  }
  return false;
}

function usedMeasureFns(draft: ReportPivotDraft, item: PlacedMeasure): MeasureFn[] {
  return draft.values.filter((m) => sameMeasureSource(m, item)).map((m) => m.fn);
}

function uniqueMeasureAlias(base: string, existing: PlacedMeasure[]): string {
  if (!existing.some((m) => m.alias === base)) return base;
  let n = 2;
  while (existing.some((m) => m.alias === `${base}_${n}`)) n += 1;
  return `${base}_${n}`;
}

export function buildFilter(
  kind: 'field' | 'meta',
  key: string,
  lookups: CatalogLookups,
): PlacedFilter | null {
  if (kind === 'meta') {
    const meta = lookups.findMeta(key);
    if (!meta || meta.role === 'measure') return null;
    const op = meta.dataType === 'date' ? 'between' : 'eq';
    return { uid: uid(), metaKey: key, op };
  }
  const field = lookups.findFieldToken(key);
  if (!field || field.role === 'none' || field.role === 'measure') return null;
  const op =
    field.dataType === 'date' ? 'between' : field.dataType === 'text' ? 'contains' : 'eq';
  return {
    uid: uid(),
    fieldId: field.fieldId,
    relationId: field.relation?.id,
    sourceDefinitionId: field.definitionId,
    op,
  };
}

export function tokenKindKey(
  token: PlacedDim | PlacedMeasure | PlacedFilter,
): { kind: 'field' | 'meta'; key: string } | null {
  if (token.metaKey) return { kind: 'meta', key: token.metaKey };
  if ('fieldId' in token && token.fieldId) {
    return {
      kind: 'field',
      key: `${token.relationId ?? 'base'}:${token.fieldId}`,
    };
  }
  return null;
}

function insertAt<T>(list: T[], item: T, at?: number): T[] {
  if (at == null || at < 0 || at > list.length) return [...list, item];
  const next = [...list];
  next.splice(at, 0, item);
  return next;
}

export function applyAddSource(
  draft: ReportPivotDraft,
  kind: 'field' | 'meta',
  key: string,
  zone: Zone,
  lookups: CatalogLookups,
  index?: number,
): ReportPivotDraft {
  if (zone === 'values') {
    const seed = buildMeasure(kind, key, lookups);
    if (!seed) return draft;
    const dataType =
      seed.metaKey === 'submittedAt'
        ? 'date'
        : seed.metaKey
          ? undefined
          : lookups.findField(seed.fieldId, seed.relationId)?.dataType;
    const allowed = measureFnsFor(dataType, seed.metaKey);
    const used = usedMeasureFns(draft, seed);
    const nextFn = allowed.find((fn) => !used.includes(fn));
    // All aggregations already placed — still allow another chip using first fn
    // so the user can change it, but prefer an unused aggregation.
    const item = buildMeasure(kind, key, lookups, nextFn ?? allowed[0]);
    if (!item) return draft;
    item.alias = uniqueMeasureAlias(item.alias || `${item.fn}_value`, draft.values);
    return { ...draft, values: insertAt(draft.values, item, index) };
  }

  if (zone === 'filters') {
    const item = buildFilter(kind, key, lookups);
    if (!item) return draft;
    const withoutDup = {
      ...draft,
      rows: draft.rows.filter((d) => !sameDim(d, item)),
      columns: draft.columns.filter((d) => !sameDim(d, item)),
      filters: draft.filters.filter((f) =>
        item.metaKey
          ? f.metaKey !== item.metaKey
          : f.fieldId !== item.fieldId || f.relationId !== item.relationId,
      ),
    };
    if (
      withoutDup.filters.some((f) =>
        item.metaKey
          ? f.metaKey === item.metaKey
          : f.fieldId === item.fieldId && f.relationId === item.relationId,
      )
    ) {
      return withoutDup;
    }
    return { ...withoutDup, filters: insertAt(withoutDup.filters, item, index) };
  }

  const item = buildDim(kind, key, lookups);
  if (!item) return draft;
  const cleared: ReportPivotDraft = {
    ...draft,
    rows: draft.rows.filter((d) => !sameDim(d, item)),
    columns: draft.columns.filter((d) => !sameDim(d, item)),
    filters: draft.filters.filter((f) =>
      item.metaKey
        ? f.metaKey !== item.metaKey
        : f.fieldId !== item.fieldId || f.relationId !== item.relationId,
    ),
  };
  if (zone === 'rows') {
    if (cleared.rows.some((d) => sameDim(d, item))) return cleared;
    return { ...cleared, rows: insertAt(cleared.rows, item, index) };
  }
  if (cleared.columns.some((d) => sameDim(d, item))) return cleared;
  return { ...cleared, columns: insertAt(cleared.columns, item, index) };
}

export function applyRemovePlaced(
  draft: ReportPivotDraft,
  zone: Zone,
  itemUid: string,
): ReportPivotDraft {
  if (zone === 'rows') return { ...draft, rows: draft.rows.filter((d) => d.uid !== itemUid) };
  if (zone === 'columns') {
    return { ...draft, columns: draft.columns.filter((d) => d.uid !== itemUid) };
  }
  if (zone === 'values') {
    return { ...draft, values: draft.values.filter((d) => d.uid !== itemUid) };
  }
  return { ...draft, filters: draft.filters.filter((d) => d.uid !== itemUid) };
}

export function applyMovePlaced(
  draft: ReportPivotDraft,
  from: Zone,
  fromUid: string,
  to: Zone,
  lookups: CatalogLookups,
  toIndex?: number,
): ReportPivotDraft {
  const listFor = (zone: Zone) =>
    zone === 'rows'
      ? draft.rows
      : zone === 'columns'
        ? draft.columns
        : zone === 'values'
          ? draft.values
          : draft.filters;

  if (from === to) {
    if (toIndex == null) return draft;
    const list = listFor(from);
    const oldIndex = list.findIndex((item) => item.uid === fromUid);
    if (oldIndex < 0) return draft;
    const reordered = arrayMove(list, oldIndex, Math.min(toIndex, list.length - 1));
    if (from === 'rows') return { ...draft, rows: reordered as PlacedDim[] };
    if (from === 'columns') return { ...draft, columns: reordered as PlacedDim[] };
    if (from === 'values') return { ...draft, values: reordered as PlacedMeasure[] };
    return { ...draft, filters: reordered as PlacedFilter[] };
  }

  const token = listFor(from).find((d) => d.uid === fromUid);
  if (!token) return draft;
  const ident = tokenKindKey(token);
  if (!ident) return draft;
  const canPlace =
    to === 'values'
      ? buildMeasure(ident.kind, ident.key, lookups)
      : to === 'filters'
        ? buildFilter(ident.kind, ident.key, lookups)
        : buildDim(ident.kind, ident.key, lookups);
  if (!canPlace) return draft;
  const without = applyRemovePlaced(draft, from, fromUid);
  return applyAddSource(without, ident.kind, ident.key, to, lookups, toIndex);
}

export function formatFilterSummary(f: PlacedFilter): string {
  const op = FILTER_OP_LABELS[f.op] || f.op;
  if (f.op === 'empty' || f.op === 'notEmpty') return op;
  if (f.op === 'between') {
    const a = f.value == null || f.value === '' ? '…' : String(f.value);
    const b = f.valueTo == null || f.valueTo === '' ? '…' : String(f.valueTo);
    return `${op} ${a} – ${b}`;
  }
  if (f.value == null || f.value === '') return op;
  if (typeof f.value === 'boolean') return `${op} ${f.value ? 'Yes' : 'No'}`;
  return `${op} ${String(f.value)}`;
}
